import graphql__e123eaf12cd223d17154 from "./__generated__/SimilarAnswersSuggestions_answer.graphql.js";import { useState, useCallback, useMemo, useEffect } from "react";
import { graphql, useFragment } from "react-relay";

import { useSimilarAnswers } from "./useSimilarAnswers";
import { SimilarAnswersContext } from "./SimilarAnswersContext";
import { useFormPageActionsVisibility } from "./useFormPageActionsVisibility";
import { usePrevious } from "../Utils/usePrevious";
import ContributionsReviewFunnel from "../ContributionsReviewFunnel/ContributionsReviewFunnel";
import SimilarAnswersSuggestionsBox from "./SimilarAnswersSuggestionsBox";
import SimilarAnswersSuggestionsLoadingIndicator from "./SimilarAnswersSuggestionsLoadingIndicator";

// Thin orchestrator and context provider for the similar-answers feature. Owns no
// funnel-specific state — it delegates fetching to useSimilarAnswers, distributes
// shared state to children via SimilarAnswersContext, and conditionally renders the
// loading indicator, the suggestions box, and the review funnel panel.
//
// The funnel lifecycle (open/close, seek-more, reset) lives here rather than in
// SimilarAnswersSuggestionsBox so that the detection effects survive the loading
// phase: SimilarAnswersSuggestionsBox unmounts while isLoading=true (the condition
// in the render below), which would wipe local isResetting/isSeekingMore flags.
//
// `children` is a render prop: it receives `handleValueChange` so the wrapped form
// field can notify the hook whenever the user's input changes.
const SimilarAnswersSuggestions = ({ children, className, ...props }) => {
  const answer = useFragment(SimilarAnswersSuggestions_answer, props.answer);

  const {
    contributionIds,
    isLoading,
    handleValueChange,
    shouldShowSuggestions,
    dismissSuggestions,
    suggestionsDismissed,
    completeSuggestionsReview,
    suggestionsReviewed,
    resetSuggestionsReview,
    seekMoreSuggestions
  } = useSimilarAnswers(answer);

  const [isFunnelOpen, setIsFunnelOpen] = useState(false);
  const [isSeekingMore, setIsSeekingMore] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [hasNoMoreSuggestions, setHasNoMoreSuggestions] = useState(false);
  const [funnelRestartKey, setFunnelRestartKey] = useState(0);

  const prevIsLoading = usePrevious(isLoading);

  // Detect when a seek-triggered fetch completes and either restart the funnel
  // with fresh suggestions or surface the "no more suggestions" state.
  useEffect(() => {
    if (!isSeekingMore || isLoading || !prevIsLoading) return;

    setIsSeekingMore(false);

    if (contributionIds.length > 0) {
      setFunnelRestartKey(k => k + 1);
      setHasNoMoreSuggestions(false);
    } else {
      setHasNoMoreSuggestions(true);
    }
  }, [isSeekingMore, isLoading, prevIsLoading, contributionIds]);

  // Detect when a reset-triggered fetch completes and auto-open the funnel if
  // new suggestions are available.
  useEffect(() => {
    if (!isResetting || isLoading || !prevIsLoading) return;

    setIsResetting(false);

    if (contributionIds.length > 0) {
      setFunnelRestartKey(k => k + 1);
      setIsFunnelOpen(true);
    }
  }, [isResetting, isLoading, prevIsLoading, contributionIds]);

  const handleOpenFunnel = useCallback(() => {
    setIsFunnelOpen(true);
    setHasNoMoreSuggestions(false);
  }, []);

  const handleCloseFunnel = useCallback(() => {
    setIsFunnelOpen(false);
  }, []);

  const handleSeeMoreSuggestions = useCallback(() => {
    setIsSeekingMore(true);
    setHasNoMoreSuggestions(false);
    seekMoreSuggestions();
  }, [seekMoreSuggestions]);

  const handleResetSuggestions = useCallback(() => {
    setIsResetting(true);
    resetSuggestionsReview();
  }, [resetSuggestionsReview]);

  const shouldHideActions =
    contributionIds.length > 0 && !suggestionsReviewed && !suggestionsDismissed;

  useFormPageActionsVisibility(!shouldHideActions);

  // Memoized so that frequent handleValueChange calls (fired on every keystroke) do not
  // cause SuggestionsBox to re-render: handleValueChange is not in the context value,
  // so consumers only re-render when the actual suggestion state changes.
  const contextValue = useMemo(
    () => ({
      contributionIds,
      suggestionsDismissed,
      suggestionsReviewed,
      dismissSuggestions,
      onOpenFunnel: handleOpenFunnel,
      onReset: handleResetSuggestions
    }),
    [
      contributionIds,
      suggestionsDismissed,
      suggestionsReviewed,
      dismissSuggestions,
      handleOpenFunnel,
      handleResetSuggestions
    ]
  );

  return (
    <SimilarAnswersContext.Provider value={contextValue}>
      {children(handleValueChange)}

      {shouldShowSuggestions && isLoading && (
        <SimilarAnswersSuggestionsLoadingIndicator className={className} />
      )}

      {shouldShowSuggestions && !isLoading && contributionIds.length > 0 && (
        <SimilarAnswersSuggestionsBox className={className} />
      )}

      {isFunnelOpen && (
        <ContributionsReviewFunnel
          contributionIds={contributionIds}
          restartKey={funnelRestartKey}
          onResume={handleCloseFunnel}
          onClose={handleCloseFunnel}
          onComplete={completeSuggestionsReview}
          onSeeMore={handleSeeMoreSuggestions}
          isSeekingMore={isSeekingMore}
          hasNoMoreSuggestions={hasNoMoreSuggestions}
        />
      )}
    </SimilarAnswersContext.Provider>
  );
};

const SimilarAnswersSuggestions_answer = graphql__e123eaf12cd223d17154;

export default SimilarAnswersSuggestions;
